import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as moment from 'moment';
import { DateRange } from 'react-date-range-updated';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe';
import SvgIcon from '@material-ui/core/SvgIcon';
import Drowdown from '@material-ui/icons/ArrowDropDown';
import Popper, { PopperProps } from '@material-ui/core/Popper';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import InputLabel from '@material-ui/core/InputLabel';

import {
	DATE_FORMAT,
	DATE_SEPARATOR,
	RANGES,
	formatDateInput,
	validateDateInput,
	validateRangeInput
} from './ranges';
import {
	Root,
	TriggerLayout,
	DateRangeLayout,
	EnterDateLayout,
	EnterDateSign,
	EnterDateInputsLayout,
	EnterDateSeparator,
	DividerLayout,
	Divider,
	TriggerInputLayout,
	PeriodName,
	PeriodNameLayout,
	DropdownLayout,
	RangeInputStyled,
	DateInputStyled,
	createDateRangeTheme,
	DateRangePickerTitleRoot,
	DateRangePickerTitleText,
	FormControlLayout,
	Hidden,
} from './styled';


const BASE_TIME_FORMAT = 'DD-MM-YYYY';
const autoCorrectedDatePipe = createAutoCorrectedDatePipe('dd/mm/yyyy');

export type RangeInputProps = {
	value: string;
	onChange: (e) => void;
} & Pick<DateRangePickerProps, 'appearance' | 'disabled' | 'readonly'>;

const RangeInput = React.forwardRef((props: RangeInputProps, ref) => {
	const {
		value,
		appearance,
		disabled,
		readonly,
		onChange,
		...rest
	} = props;
	
	return (
		<RangeInputStyled
			ref={ref}
			mask={[/[0-3]/,/\d/,'.',/(0|1)/,/\d/,'.',/(1|2)/,/\d/,/\d/,/\d/,'—',/[0-3]/,/\d/,'.',/(0|1)/,/\d/,'.',/(1|2)/,/\d/,/\d/,/\d/]}
			keepCharPositions
			showMask
			pipe={autoCorrectedDatePipe}
			appearance={appearance}
			value={value}
			readOnly={readonly}
			disabled={disabled}
			onChange={onChange}
			{...rest}
		/>
	);
});
const DateInput = React.forwardRef((props: any, ref) => {
	const { value, onChange, ...rest } = props;

	return (
		<DateInputStyled
			ref={ref}
			mask={[/[0-3]/,/\d/,'.',/(0|1)/,/\d/,'.',/(1|2)/,/\d/,/\d/,/\d/]}
			keepCharPositions
			showMask
			pipe={autoCorrectedDatePipe}
			value={value}
			onChange={onChange}
			{...rest} />
	);
});

export type DateRangePickerProps = {
	appearance?: 'title' | 'form-control' | 'popper';
	dateStart?: string;
	dateEnd?: string;
	readonly?: boolean;
	disabled?: boolean;
	label?: React.ReactNode;
	syncDates?: boolean;
	id?: string;
	renderTrigger?: ({ handleOpen: Function }) => React.ReactNode;
	RootProps?: {
		fullWidth?: boolean;
	};
	PopperProps: {
		animationFrom: 'start' | 'end';
	} & PopperProps;
	onChange: (dateStart: string, dateEnd: string) => void;
}

type DateRangePickerState = {
	isOpen: boolean;
	popoverAnchorEl: HTMLElement;
	dateStart: string;
	dateEnd: string;
};

class DateRangePicker extends React.PureComponent<DateRangePickerProps, DateRangePickerState> {
	static defaultProps = {
		appearance: 'form-control',
		dateStart: moment().format(DATE_FORMAT),
		dateEnd: moment().format(DATE_FORMAT),
		RootProps: {},
		PopperProps: {
			placement: 'bottom-start',
			animationFrom: 'start',
		},
	};
	state = {
		isOpen: false,
		popoverAnchorEl: null,
		dateStart: moment(this.props.dateStart, BASE_TIME_FORMAT).format(DATE_FORMAT) || DateRangePicker.defaultProps.dateStart,
		dateEnd: moment(this.props.dateEnd, BASE_TIME_FORMAT).format(DATE_FORMAT) || DateRangePicker.defaultProps.dateEnd,
	};
	dateRangeRef = React.createRef();
	inputRef = React.createRef<{ inputElement: HTMLInputElement }>();
	hiddenContentRef = React.createRef<HTMLElement>();

	componentDidMount() {
		this.patchInputWidth();
		setTimeout(() => this.patchInputWidth(), 1000);
		window.addEventListener('resize', this.handleResize);
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		if (nextProps.syncDates) {
			if (nextProps.dateStart !== this.state.dateStart) {
				this.setState({ dateStart: nextProps.dateStart });
			}

			if (nextProps.dateEnd !== this.state.dateEnd) {
				this.setState({ dateEnd: nextProps.dateEnd });
			}
		}
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.handleResize);
	}

	handleResize = () => {
		this.patchInputWidth();
	};

	patchInputWidth = () => {
		if (!this.hiddenContentRef.current || !this.inputRef.current) return;
		const width = this.hiddenContentRef.current.clientWidth;

		this.inputRef.current.inputElement.style.setProperty('width', `${width}px`);
	};

	controlDatePickerClickHandlers = (addEvent: boolean) => {
		if (this.dateRangeRef.current) {
			const rootNode = ReactDOM.findDOMNode(this.dateRangeRef.current as any) as HTMLElement;
			const nodeList: NodeListOf<HTMLLinkElement> = rootNode.querySelectorAll('.rdr-PredefinedRangesItem');

			[].slice.call(nodeList).forEach(node => {
				addEvent && node.addEventListener('click', this.handleClose);
				!addEvent && node.removeEventListener('click', this.handleClose);
			});
		}
	}

	handleOpen = ev => {
		const { readonly, disabled } = this.props;

		ev.preventDefault();

		if (readonly || disabled) return;

		this.setState({ isOpen: true, popoverAnchorEl: ev.currentTarget },
			() => setTimeout(() => this.controlDatePickerClickHandlers(true))
		);
	}

	handleClose = () => 
		this.setState({ isOpen: false, popoverAnchorEl: null }, () => this.controlDatePickerClickHandlers(false))

	emitOnChangeEvent = (dateStart: string, dateEnd: string): void => {
		const dateStartMoment = moment(dateStart, DATE_FORMAT);
		const dateEndMoment = moment(dateEnd, DATE_FORMAT);

		if (dateStartMoment.isSame(dateEndMoment) || dateStartMoment.isBefore(dateEndMoment)) {
			this.props.onChange(dateStartMoment.format(BASE_TIME_FORMAT), dateEndMoment.format(BASE_TIME_FORMAT));
		}
	}

	handleChangeDate = range => {
		const dateStart = range.startDate.format(DATE_FORMAT);
		const dateEnd = range.endDate.format(DATE_FORMAT);

		this.setState({ dateStart, dateEnd });
		this.emitOnChangeEvent(dateStart, dateEnd);
	}

	handleSetDatesWithRangeInput = ev => {
		const { value } = ev.target;
		
		if (validateDateInput(value)) {
			const dates = value.split(DATE_SEPARATOR);
			const dateStart = dates[0].trim();
			const dateEnd = dates[1].trim();
			
			this.setState({ dateStart, dateEnd }, () => {
				const isValidDateStart = moment(dateStart, DATE_FORMAT).isValid();
				const isValidDateEnd = moment(dateEnd, DATE_FORMAT).isValid();
				const isValidDates = isValidDateStart && isValidDateEnd;

				if (isValidDates) {
					const { dateStart, dateEnd } = this.state;
	
					this.emitOnChangeEvent(dateStart, dateEnd);
				}
			});
		}
	}

	handleSetDatesWithDateInputs = ev => {
		const { name, value } = ev.target;

		if (validateRangeInput(value)) {
			this.setState({
				[name]: value,
			} as any, () => {
				const isValidDate = moment(value, DATE_FORMAT).isValid();

				if (isValidDate) {
					const {
						dateStart,
						dateEnd
					} = this.state;

					this.emitOnChangeEvent(dateStart, dateEnd);
				}
			});
		}
	}

	renderDateRange = () => {
		const { dateStart, dateEnd } = this.state;
		const dateRangeTheme = createDateRangeTheme();
		const setDateStart = () => dateStart;
		const setDateEnd = () => dateEnd;

		return (
			<>
				<EnterDateLayout>
					<EnterDateSign>Выбор периода</EnterDateSign>
					<EnterDateInputsLayout>
						<DateInput
							value={dateStart}
							name='dateStart'
							onChange={this.handleSetDatesWithDateInputs} 
						/>
						<EnterDateSeparator>—</EnterDateSeparator>
						<DateInput
							value={dateEnd}
							name='dateEnd'
							onChange={this.handleSetDatesWithDateInputs}
						/>
					</EnterDateInputsLayout>
				</EnterDateLayout>
				<DividerLayout>
					<Divider />
				</DividerLayout>
				<DateRange
					ref={this.dateRangeRef}
					startDate={setDateStart}
					endDate={setDateEnd}
					format={DATE_FORMAT}
					lang='ru'
					calendars={2}
					firstDayOfWeek={1}
					ranges={RANGES}
					rangedCalendars
					onChange={this.handleChangeDate}
					theme={dateRangeTheme}
				/>
			</>
		);
	}

	renderTitleTrigger = () => {
		const { dateStart, dateEnd } = this.state;
		let periodName = '';

		Object.keys(RANGES).forEach(key => {
			if (moment(dateStart, BASE_TIME_FORMAT).isSame(RANGES[key].startDate(moment()), 'day')
			&& moment(dateEnd, BASE_TIME_FORMAT).isSame(RANGES[key].endDate(moment()), 'day')) {
				periodName = key.toLocaleLowerCase();
			}
		});

		return (
			<TriggerLayout onClick={this.handleOpen}>
				<TriggerInputLayout>
					<Hidden ref={this.hiddenContentRef}>{formatDateInput(dateStart, dateEnd)}</Hidden>
					{
						periodName &&
						<PeriodNameLayout>
							<PeriodName>
								{`${periodName}\u00A0`}
							</PeriodName>
						</PeriodNameLayout>
					}
					<span>(</span>
						<RangeInput
							ref={this.inputRef}
							value={formatDateInput(dateStart, dateEnd)}
							onChange={this.handleSetDatesWithRangeInput}
						/>
					<span>)</span>
					<DropdownLayout>
						<SvgIcon>
							<Drowdown />
						</SvgIcon>
					</DropdownLayout>
				</TriggerInputLayout>
			</TriggerLayout>
		);
	};

	renderFormControlTrigger = () => {
		const { appearance, label, id, disabled, readonly } = this.props;
		const { dateStart, dateEnd } = this.state;

		return (
			<FormControlLayout onClick={this.handleOpen}>
				<InputLabel htmlFor={id} shrink style={{ marginBottom: 8 }}>{label}</InputLabel>
				<RangeInput
					appearance={appearance}
					disabled={disabled}
					readonly={readonly}
					value={formatDateInput(dateStart, dateEnd)}
					onChange={this.handleSetDatesWithRangeInput}
				/>
			</FormControlLayout>
		);
	};
	
	render() {
		const { appearance, renderTrigger, RootProps, PopperProps } = this.props;
		const { isOpen, popoverAnchorEl } = this.state;
		const { animationFrom, ...restPopperProps } = PopperProps;
		const transformOrigin = animationFrom === 'start' ? '0 0 0' : '100% 0 0';

		return (
			<Root {...RootProps}>
				{
					appearance === 'title' &&
					this.renderTitleTrigger()
				}
				{
					appearance === 'form-control' &&
					this.renderFormControlTrigger()
				}
				{
					typeof renderTrigger === 'function' &&
					renderTrigger({ handleOpen: this.handleOpen} )
				}
				<Popper
					{...restPopperProps}
					open={isOpen}
					anchorEl={popoverAnchorEl}
					style={{ zIndex: 1000 }}
					transition>
					{({ TransitionProps }) => (
						<ClickAwayListener onClickAway={this.handleClose}>
							<Grow {...TransitionProps} in={isOpen} style={{ transformOrigin, width: 'auto' }}>
								<Paper square elevation={1}>
									<DateRangeLayout>
										{this.renderDateRange()}
									</DateRangeLayout>
								</Paper>
							</Grow>
						</ClickAwayListener>
					)}
				</Popper>
			</Root>
		);
	}
}

type DateRangePickerTitleProps = {
	text: string;
};

const DateRangePickerTitle: React.SFC<DateRangePickerTitleProps> = ({ text, children }) => {
	return (
		<DateRangePickerTitleRoot>
			<DateRangePickerTitleText>{text}</DateRangePickerTitleText>
			{'\u00A0'}{'\u00A0'}
			{children}
		</DateRangePickerTitleRoot>
	);
}

DateRangePickerTitle.displayName = 'DateRangePickerTitle';
DateRangePickerTitle.defaultProps = {};

export default DateRangePicker;
export {
	DateRangePickerTitle
};
